import { useMemo, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { AUTO_REFRESH_OPTIONS } from 'utils/common-util';
import { RefreshIcon, VectorIcon } from 'components/CommonIcons/Icons';
import { useTheme } from '@mui/material/styles';

interface AutoRefreshProps {
    onIntervalChange: (minutes: number | null) => void;
    autoRefreshInterval: number | null;
}

const AutoRefresh = ({ onIntervalChange, autoRefreshInterval,  }: AutoRefreshProps) => {
    const theme = useTheme();
    const p = theme.palette;
    const selectedLabel = useMemo(() => {
        const selectedOption = AUTO_REFRESH_OPTIONS.find(opt => opt.value === autoRefreshInterval);
        if (!selectedOption) return '';
        if (selectedOption.value === 0) return selectedOption.label;
        return `Every ${selectedOption.label}`;
    }, [autoRefreshInterval]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleIconClick = (event: React.MouseEvent) => {
        setAnchorEl(event.currentTarget as HTMLElement);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (value: number | null) => {
        onIntervalChange(value);
        handleMenuClose();
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={"Auto Refresh Settings"} placement="bottom">
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleIconClick}>
                    <Typography
                        sx={{
                            color: "#48B5ED",
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: "Montserrat",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {selectedLabel}
                    </Typography>
                    <IconButton size="small">
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 250,
                            borderRadius: 2,
                            boxShadow: 3,
                            backgroundColor: '#FBFDFF',
                            p: 1,
                        },
                    },
                }}
                MenuListProps={{
                    sx: {
                        backgroundColor: `${p.backgroundLight.secondary} !important`,
                        color: `${p.textLight.dark} !important`,
                    },
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Typography
                    sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        fontFamily: 'Montserrat',
                        color: '#000000',
                        px: 2,
                        pt: 1,
                        pb: 0.5,
                    }}
                >
                    Auto Refresh In
                </Typography>
                {AUTO_REFRESH_OPTIONS.map(opt => (
                    <MenuItem
                        key={opt.value}
                        selected={autoRefreshInterval === opt.value}
                        onClick={() => handleMenuItemClick(opt.value)}
                        sx={{
                            fontWeight: 400,
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            fontStyle: opt.value === 0 ? 'italic' : 'normal',
                            color: '#000000',
                            py: 1.2,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span>{opt.label}</span>
                        {autoRefreshInterval === opt.value && (
                            <VectorIcon />
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
  );
};

export default AutoRefresh;
